import type { Database } from "@nifgash/shared/db/types";
import { api, getSession } from "./client";

export type EventRow = Database["public"]["Tables"]["events"]["Row"];

export type AttendeePreview = {
  user_id: string;
  full_name: string | null;
  photo_url: string | null;
};

export type EventCard = EventRow & {
  going_count: number;
  my_status: "going" | "cancelled" | null;
  attendees: AttendeePreview[] | null;
  // lat/lng come from migration 0006; the API already returns them on
  // /api/events. Declared here (like PublicEventCard) until the generated
  // EventRow type catches up, so the map view can plot these cards.
  lat: number | null;
  lng: number | null;
  // Signed cover-image URL (migration 0008), null when the host didn't add one.
  image_url: string | null;
};

export type EventsResponse = {
  events: EventCard[];
  is_premium: boolean;
};

export async function listEvents(
  opts: { city?: string; formats?: string[] } = {},
): Promise<EventsResponse> {
  const formatSet = opts.formats && opts.formats.length > 0 ? new Set(opts.formats) : null;
  // Anonymous visitors get the public list and we filter client-side.
  if (!getSession()) {
    const pub = await listPublicEvents();
    const filtered = pub.events.filter((e) => {
      if (opts.city && e.city !== opts.city) return false;
      if (formatSet && (!e.format || !formatSet.has(e.format))) return false;
      return true;
    });
    return {
      events: filtered.map((e) => ({ ...e, my_status: null })),
      is_premium: false,
    };
  }
  const params = new URLSearchParams();
  if (opts.city) params.set("city", opts.city);
  // Multiple formats are passed as a comma-separated list (?format=sport,run).
  if (formatSet) params.set("format", [...formatSet].join(","));
  const qs = params.toString();
  return api<EventsResponse>(`/api/events${qs ? `?${qs}` : ""}`);
}

/** Public list (no auth required) — used by landing + map.
 * lat/lng are added by migration 0006; declared here until generated types catch up. */
export type PublicEventCard = EventRow & {
  going_count: number;
  attendees: AttendeePreview[];
  lat: number | null;
  lng: number | null;
  image_url: string | null;
};

export async function listPublicEvents(): Promise<{ events: PublicEventCard[] }> {
  return api<{ events: PublicEventCard[] }>("/api/public/events");
}

export type PublicEventDetail = {
  event: EventRow & { lat: number | null; lng: number | null; image_url: string | null };
  going_count: number;
  attendees: AttendeePreview[];
};

export async function getPublicEvent(id: string): Promise<PublicEventDetail> {
  return api<PublicEventDetail>(`/api/public/events/${id}`);
}

type RpcOk = { ok: true; going_count: number; status: "going" | "cancelled" };

export async function setRsvp(event_id: string, going: boolean): Promise<RpcOk> {
  return api<RpcOk>("/api/rsvp", {
    method: "POST",
    body: JSON.stringify({ event_id, going }),
  });
}

/** Upload a cover image for a freshly-created event (multipart). */
export async function uploadEventImage(eventId: string, file: File): Promise<{ image_url: string | null }> {
  const fd = new FormData();
  fd.append("image", file);
  return api<{ image_url: string | null }>(`/api/events/${eventId}/image`, {
    method: "POST",
    body: fd,
  });
}
